<main class="planner-shell">
  <header class="planner-header">
    <div class="header-main">
      <div class="breadcrumb">
        <dx-button class="back-button" stylingMode="text" text="Back to promotions" [disabled]="saving" (onClick)="backToPromotions()"></dx-button>
        <span aria-hidden="true">/</span>
        <span class="breadcrumb-current">{{ promotion.promotion_name || promotion.name || 'Promotion' }}</span>
      </div>
      <h1>Catalogue planning</h1>
      <p class="page-description">Organise pages and authorised product placements for this promotion.</p>
    </div>
    <div class="header-actions">
      <span class="saved-state" [class.is-dirty]="dirty">{{ saving ? 'Saving…' : dirty ? 'Unsaved changes' : notice || 'All changes saved' }}</span>
      <dx-button class="save-button" type="default" stylingMode="contained" [text]="saving ? 'Saving' : 'Save changes'"
        [disabled]="!dirty || saving || editingLocked" (onClick)="save()"></dx-button>
    </div>
  </header>

  <div *ngIf="loading" class="loading-layout" aria-label="Loading catalogue">
    <div class="skeleton sidebar-skeleton"></div><div class="skeleton main-skeleton"><div></div><div></div><div></div></div>
  </div>
  <section *ngIf="!loading && loadError" class="message-card error-card">
    <span class="message-icon">!</span>
    <div>
      <h2>Catalogue unavailable</h2>
      <p>{{ loadError }}</p>
      <dx-button type="default" text="Retry loading" [disabled]="saving" (onClick)="load()"></dx-button>
    </div>
  </section>

  <div *ngIf="!loading && !loadError" class="planner-grid">
    <aside class="page-rail">
      <div class="rail-heading">
        <div><p class="section-label">Catalogue structure</p><h2>Pages <span>{{ pages.length }} / {{ limits.maxPages }}</span></h2></div>
        <dx-button icon="plus" hint="Add page" aria-label="Add page" [disabled]="editingLocked || saving" (onClick)="addPage()"></dx-button>
      </div>
      <div *ngIf="!pages.length" class="empty-pages">
        <strong>No pages yet</strong>
        <p>Create a page to start organising this catalogue.</p>
        <dx-button stylingMode="text" text="Create first page" [disabled]="editingLocked || saving" (onClick)="addPage()"></dx-button>
      </div>
      <div class="page-navigation">
        <dx-text-box class="page-search" label="Find a page" labelMode="static" placeholder="Page name or number"
          valueChangeEvent="input" [showClearButton]="true" [value]="pageSearchQuery"
          (onValueChanged)="updatePageSearch($event.value)" aria-label="Find a page by name or page number"></dx-text-box>
        <dx-select-box id="page-selection" class="page-jump" label="Go to page" labelMode="static" [items]="pageOptions"
          valueExpr="index" displayExpr="text" [searchEnabled]="true" searchExpr="text"
          [placeholder]="pages.length ? 'Choose a page' : 'No pages yet'"
          [value]="activePageIndex >= 0 ? activePageIndex + 1 : null" [disabled]="!pages.length || saving"
          (onValueChanged)="$event.event && goToPage($event.value)"></dx-select-box>
        <div class="page-stepper" aria-label="Booklet page navigation">
          <dx-button text="Previous" stylingMode="outlined" [disabled]="activePageIndex <= 0 || saving" (onClick)="previousPage()"></dx-button>
          <span aria-live="polite">{{ pages.length ? (activePageIndex + 1) + ' of ' + pages.length : '0 of 0' }}</span>
          <dx-button text="Next" stylingMode="outlined" [disabled]="activePageIndex < 0 || activePageIndex >= pages.length - 1 || saving" (onClick)="nextPage()"></dx-button>
        </div>
        <p *ngIf="pageSearchQuery && !filteredPages.length" class="page-search-empty" role="status">No pages match “{{ pageSearchQuery }}”.</p>
      </div>
      <div class="page-list" role="list" [attr.aria-label]="'Catalogue pages, ' + pages.length + ' total'">
        <article *ngFor="let page of filteredPages; trackBy: trackPage" class="page-card" role="listitem"
          [attr.id]="'planner-page-' + page.id" [class.active]="activePageId === page.id" (click)="selectPage(page.id)">
          <button type="button" class="page-main" [disabled]="saving" [attr.aria-current]="activePageId === page.id ? 'page' : null" [attr.aria-label]="'Select page ' + (page.name || 'untitled')">
            <span class="page-number">{{ pageNumbers.get(page.id)!.toString().padStart(2, '0') }}</span>
            <span class="page-detail">
              <strong>{{ page.name || 'Untitled page' }}</strong>
              <small>{{ page.category || page.products.length + ' products' }}</small>
              <dx-progress-bar class="capacity-line" [min]="0" [max]="page.capacity" [value]="page.products.length" [showStatus]="false"></dx-progress-bar>
            </span>
            <span class="page-count">{{ page.products.length }}/{{ page.capacity }}</span>
          </button>
          <div class="page-controls" (click)="$event.stopPropagation()">
            <dx-button icon="arrowup" hint="Move page up" aria-label="Move page up" stylingMode="text" [disabled]="editingLocked || saving || pageNumbers.get(page.id) === 1" (onClick)="movePage(pageNumbers.get(page.id)! - 1, -1)"></dx-button>
            <dx-button icon="arrowdown" hint="Move page down" aria-label="Move page down" stylingMode="text" [disabled]="editingLocked || saving || pageNumbers.get(page.id) === pages.length" (onClick)="movePage(pageNumbers.get(page.id)! - 1, 1)"></dx-button>
            <dx-button icon="close" hint="Remove page" aria-label="Remove page" stylingMode="text" type="danger" [disabled]="editingLocked || saving" (onClick)="removePage(page)"></dx-button>
          </div>
        </article>
      </div>
      <div class="rail-note"><p>Products can only be placed once per promotion. Open slots remain unassigned until a product is selected.</p></div>
    </aside>

    <section class="page-editor" *ngIf="activePage as page; else selectOrCreate">
      <div class="editor-heading">
        <div>
          <p class="section-label">Page {{ activePageIndex + 1 }}</p>
          <h2>{{ page.name || 'Name this page' }}</h2>
          <p class="subheading">Set page details and arrange products in display order.</p>
        </div>
        <span class="capacity-badge" [class.full]="openSlots === 0">{{ openSlots === 0 ? 'At capacity' : openSlots + ' open slots' }}</span>
      </div>
      <div *ngIf="editingLocked" class="locked-notice"><strong>Read-only promotion</strong><span>{{ lockedReason || 'This promotion is no longer editable. Your current local edits remain visible.' }}</span></div>
      <div class="page-fields">
        <dx-text-box label="Page name" labelMode="static" placeholder="For example, Bright whites" [maxLength]="limits.maxPageNameLength"
          [value]="page.name" valueChangeEvent="input" [disabled]="editingLocked || saving"
          (onValueChanged)="$event.event && renamePage(page, $event.value)"></dx-text-box>
        <div class="capacity-field">
          <dx-number-box label="Slot capacity" labelMode="static" [min]="page.products.length || 1" [max]="limits.maxCapacity" [step]="1"
            [showSpinButtons]="true" [value]="page.capacity" [disabled]="editingLocked || saving"
            (onValueChanged)="$event.event && updateCapacity(page, $event.value, $event.component)"></dx-number-box>
          <small>Minimum {{ page.products.length || 1 }} · Maximum {{ limits.maxCapacity | number }}</small>
          <small *ngIf="capacityError" class="capacity-error" role="alert" aria-live="polite">{{ capacityError }}</small>
        </div>
      </div>
      <div class="capacity-meter">
        <span>Placement capacity</span>
        <strong>{{ page.products.length }} <small>/ {{ page.capacity }}</small></strong>
        <dx-progress-bar class="meter-bar" [min]="0" [max]="page.capacity" [value]="page.products.length" [showStatus]="false"></dx-progress-bar>
      </div>
      <div class="products-heading">
        <div><p class="section-label">Page contents</p><h3>Product order <span>{{ page.products.length }}</span></h3></div>
        <span class="table-label">Use arrows to change order</span>
      </div>
      <div class="product-slots">
        <article *ngFor="let product of placedProducts; let i = index; trackBy: trackPlacement" class="product-row">
          <span class="slot-number">{{ (i + 1).toString().padStart(2, '0') }}</span>
          <span class="product-glyph">{{ product.code.slice(0, 1) }}</span>
          <span class="product-copy"><strong>{{ product.name }}</strong><small>{{ product.code }} <i>·</i> {{ product.category }}</small><small *ngIf="product.ineligible" class="ineligible-note">No longer eligible — remove before saving</small></span>
          <div class="row-actions">
            <dx-button icon="arrowup" stylingMode="text" [disabled]="editingLocked || saving || i === 0" [attr.aria-label]="'Move ' + product.name + ' up'" (onClick)="moveProduct(i, -1)"></dx-button>
            <dx-button icon="arrowdown" stylingMode="text" [disabled]="editingLocked || saving || i === placedProducts.length - 1" [attr.aria-label]="'Move ' + product.name + ' down'" (onClick)="moveProduct(i, 1)"></dx-button>
            <dx-button icon="close" stylingMode="text" type="danger" [disabled]="editingLocked || saving" [attr.aria-label]="'Remove ' + product.name" (onClick)="removeProduct(product.productId)"></dx-button>
          </div>
        </article>
        <div *ngIf="openSlots > 0" class="empty-slot"><span class="slot-ring"></span><span>+{{ openSlots | number }} open {{ openSlots === 1 ? 'placement' : 'placements' }}</span></div>
        <div *ngIf="openSlots === 0" class="capacity-note">This page is full. Increase capacity to place another product.</div>
      </div>
      <div class="product-picker">
        <div>
          <p class="section-label">Authorised products</p>
          <small class="picker-hint">{{ !products.length ? 'No authorised products'
            : availableProducts.length ? (availableProducts.length | number) + ' available to place'
            : 'Every authorised product is placed' }}</small>
        </div>
        <dx-button id="add-products" class="add-product" type="default" icon="plus" text="Add products"
          [disabled]="!canAddProducts" (onClick)="openProductSelection()"></dx-button>
        <small *ngIf="limitError" class="limit-error" role="alert">{{ limitError }}</small>
      </div>
    </section>
  <ng-template #selectOrCreate>
    <section class="page-editor empty-editor">
      <p class="section-label">Catalogue contents</p>
      <h2>No catalogue pages</h2>
      <p>Create a page to begin arranging products for this promotion.</p>
      <dx-button type="default" text="Create a page" [disabled]="editingLocked || saving" (onClick)="addPage()"></dx-button>
    </section>
  </ng-template>
  </div>

  <!-- Shared product-selection grid; selected rows are placed on the active page (not saved until Save). -->
  <dx-popup
    [(visible)]="productSelectionVisible"
    [hideOnOutsideClick]="true"
    [showTitle]="true"
    [title]="'Add products to ' + (activePage?.name || 'this page')"
    height="75%"
    width="70%"
    [showCloseButton]="true">
    <app-product-selection parent="catalogue-planner"
      (productsSelected)="addProducts($event)"
      (closeChild)="closeProductSelection()"></app-product-selection>
  </dx-popup>

  <div *ngIf="saveError" class="floating-message error-message" role="alert">
    <span>!</span>
    <div class="message-body">
      <p>{{ saveError }}</p>
      <ul *ngIf="validationMessages.length" class="field-errors">
        <li *ngFor="let line of validationMessages">{{ line }}</li>
      </ul>
    </div>
    <dx-button *ngIf="conflict" class="conflict-reload" stylingMode="outlined" text="Reload server version" (onClick)="reloadAfterConflict()"></dx-button>
    <dx-button icon="close" stylingMode="text" aria-label="Dismiss" (onClick)="dismissError()"></dx-button>
  </div>
  <div *ngIf="notice && !dirty" class="floating-message success-message" role="status">
    <span>✓</span><p>{{ notice }}</p>
    <dx-button icon="close" stylingMode="text" aria-label="Dismiss" (onClick)="notice = ''"></dx-button>
  </div>
</main>
